import {
    ChangeEvent,
    FunctionComponent,
    ReactElement,
    useCallback,
    useState,
} from "react";

import {
    Button,
    Chip,
    MenuItem,
    Select,
    SelectChangeEvent,
    TextField,
    Typography,
    styled,
} from "@mui/material";

import {
    DataGrid,
    GridColDef,
    GridRenderCellParams,
    GridRowParams,
    GridToolbar,
    GridValueGetterParams,
} from "@mui/x-data-grid";
import { format } from "date-fns";
import { Link, useNavigate } from "react-router-dom";

import { EmptyRecords } from "../../components/common";
import { useGetRecipes } from "../../hooks";
import { globalDateTimeFormat } from "../../utils/constants";
import {
    RecipeDietType,
    RecipeDifficulty,
    RecipeStatus,
    recipeDietTypeNames,
    recipeDifficultyNames,
    recipeStatusNames,
} from "../../utils/enums";

const Root = styled("main")`
    padding: ${({ theme }) => theme.spacing(3)};
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing(3)};
`;

const TitleContainer = styled("div")`
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const Title = styled(Typography)`
    font-size: 24px;
    font-weight: 500;
`;

const FiltersContainer = styled("div")`
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing(2)};
`;

const EmptyRecordsContainer = styled("div")`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing(1)};
    align-items: center;
`;

const statusChipColor: Record<RecipeStatus, "default" | "success" | "warning"> =
    {
        [RecipeStatus.DRAFT]: "warning",
        [RecipeStatus.PUBLISHED]: "success",
        [RecipeStatus.ARCHIVED]: "default",
    };

export const ViewRecipesScreen: FunctionComponent = (): ReactElement => {
    const navigate = useNavigate();
    const [q, setQ] = useState("");
    const [status, setStatus] = useState<RecipeStatus | "">("");

    const recipesQuery = useGetRecipes({
        q: q || undefined,
        status: status === "" ? undefined : status,
    });

    const handleSearchChange = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            setQ(event.target.value);
        },
        [],
    );

    const handleStatusChange = useCallback(
        (event: SelectChangeEvent<RecipeStatus | "">) => {
            setStatus(event.target.value as RecipeStatus | "");
        },
        [],
    );

    const handleRowClick = useCallback(
        (params: GridRowParams) => {
            navigate(`/recipes/${params.row.id}`);
        },
        [navigate],
    );

    const columns: GridColDef[] = [
        {
            field: "title",
            headerName: "Title",
            flex: 3,
            minWidth: 260,
        },
        {
            field: "status",
            headerName: "Status",
            flex: 1,
            minWidth: 140,
            renderCell: (params: GridRenderCellParams) => (
                <Chip
                    size="small"
                    label={recipeStatusNames[params.row.status]}
                    color={statusChipColor[params.row.status as RecipeStatus]}
                />
            ),
        },
        {
            field: "difficulty",
            headerName: "Difficulty",
            flex: 1,
            valueGetter: (params: GridValueGetterParams) =>
                recipeDifficultyNames[
                    params.row.difficulty as RecipeDifficulty
                ],
        },
        {
            field: "dietType",
            headerName: "Diet",
            flex: 1,
            valueGetter: (params: GridValueGetterParams) =>
                recipeDietTypeNames[params.row.dietType as RecipeDietType],
        },
        {
            field: "totalTimeMinutes",
            headerName: "Total time",
            flex: 1,
            valueGetter: (params: GridValueGetterParams) =>
                `${params.row.totalTimeMinutes}m`,
        },
        {
            field: "updatedAt",
            headerName: "Updated on",
            flex: 1,
            valueGetter: (params: GridValueGetterParams) =>
                `${format(
                    new Date(params.row.updatedAt),
                    globalDateTimeFormat,
                )}`,
        },
    ];

    return (
        <Root>
            <TitleContainer>
                <Title>Recipes</Title>
                <FiltersContainer>
                    <TextField
                        size="small"
                        placeholder="Search recipes"
                        value={q}
                        onChange={handleSearchChange}
                    />
                    <Select
                        size="small"
                        displayEmpty
                        value={status}
                        onChange={handleStatusChange}
                        sx={{ minWidth: 140 }}
                    >
                        <MenuItem value="">All statuses</MenuItem>
                        {Object.values(RecipeStatus)
                            .filter(
                                (value): value is RecipeStatus =>
                                    typeof value === "number",
                            )
                            .map((value) => (
                                <MenuItem key={value} value={value}>
                                    {recipeStatusNames[value]}
                                </MenuItem>
                            ))}
                    </Select>
                    <Link to="/recipes/new">
                        <Button variant="contained">Create recipe</Button>
                    </Link>
                </FiltersContainer>
            </TitleContainer>

            {(recipesQuery?.data?.records ?? []).length === 0 && (
                <EmptyRecords>
                    <EmptyRecordsContainer>
                        <Title>Manage recipes</Title>
                        <Link to="/recipes/new">
                            <Button variant="contained">Create recipe</Button>
                        </Link>
                    </EmptyRecordsContainer>
                </EmptyRecords>
            )}

            {(recipesQuery?.data?.records ?? []).length > 0 && (
                <DataGrid
                    rows={recipesQuery?.data?.records ?? []}
                    columns={columns}
                    initialState={{
                        pagination: {
                            paginationModel: {
                                pageSize: 20,
                            },
                        },
                    }}
                    onRowClick={handleRowClick}
                    pageSizeOptions={[10, 20, 30]}
                    disableRowSelectionOnClick={true}
                    slots={{ toolbar: GridToolbar }}
                    slotProps={{
                        toolbar: {
                            showQuickFilter: true,
                        },
                    }}
                    disableColumnSelector={true}
                    disableDensitySelector={true}
                />
            )}
        </Root>
    );
};
